import { otpsSend } from '@zealot/shared/apiClient';
import { getApiErrorMessage } from '@zealot/shared/errors';
import { Button, Form, FormField, Input, Stack, useZodForm } from '@zealot/ui';
import { z } from 'zod';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useCooldownRemaining } from './useCooldownRemaining';

const SEND_FAILED_MESSAGE = 'Failed to send verification code';

const sendCodeSchema = z.object({
  email: z.string().trim().pipe(z.email('Please enter a valid email address')),
});

type SendCodeValues = z.infer<typeof sendCodeSchema>;

type OtpSendStepProps = {
  initialEmail?: string;
  primaryColor: string;
  onSent: (email: string) => void;
  onError: (message: string) => void;
  lastSentAt: number | null;
  cooldownSeconds?: number;
};

export const OtpSendStep = ({
  initialEmail = '',
  primaryColor,
  onSent,
  onError,
  lastSentAt,
  cooldownSeconds = 60,
}: OtpSendStepProps) => {
  const client = useApiClient();
  const form = useZodForm({ schema: sendCodeSchema, defaultValues: { email: initialEmail }, mode: 'onSubmit' });
  const cooldownRemaining = useCooldownRemaining(lastSentAt, cooldownSeconds);
  const { isSubmitting } = form.formState;
  const email = form.watch('email');

  const handleValidSubmit = async ({ email: validEmail }: SendCodeValues) => {
    try {
      const response = await otpsSend({ client, body: { email: validEmail } });

      if (response.error || (response.response?.status ?? 0) >= 400) {
        const apiError = response.error as { code?: string; message?: string } | undefined;
        if (apiError?.code === 'USER_BANNED') {
          onError(getApiErrorMessage(apiError, 'This account has been banned.'));
          return;
        }
        form.setError('email', { message: getApiErrorMessage(apiError, SEND_FAILED_MESSAGE) });
        return;
      }

      onSent(validEmail);
    } catch (err) {
      form.setError('email', { message: getApiErrorMessage(err, SEND_FAILED_MESSAGE) });
    }
  };

  const getSubmitLabel = () => {
    if (isSubmitting) return 'Sending...';
    if (cooldownRemaining > 0) return `Resend in ${cooldownRemaining}s`;
    return 'Send verification code';
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleValidSubmit)} noValidate>
        <Stack gap={6}>
          <FormField
            control={form.control}
            name="email"
            label="Email address"
            render={({ field }) => (
              <Input type="email" placeholder="you@example.com" disabled={isSubmitting} autoFocus {...field} />
            )}
          />
          <Button
            type="submit"
            className="w-full"
            style={{ backgroundColor: primaryColor }}
            disabled={isSubmitting || !email || cooldownRemaining > 0}
          >
            {getSubmitLabel()}
          </Button>
        </Stack>
      </form>
    </Form>
  );
};
